import React, { useState } from "react";
import { Lock, Plus, ChevronDown, ChevronUp, Trash2, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { fmtMoney, STATUS_TONE } from "../../lib/salary";
import PaymentDialog from "./PaymentDialog.jsx";

export default function SalaryMonthTable({ rows, period, onChanged }) {
  const [payFor, setPayFor] = useState(null);
  const [open, setOpen] = useState({});
  const nav = useNavigate();

  const removePayment = async (p) => {
    const msg = p.transaction_id
      ? "Delete this salary payment? The linked Money Paid transaction in the register will also be deleted."
      : "Delete this salary payment?";
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/salary/payments/${p.id}`);
      toast.success("Payment deleted");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (rows.length === 0) {
    return <div className="border border-slate-200 rounded-xl bg-white p-8 text-sm text-slate-400 text-center" data-testid="salary-empty">No salaries due for this month.</div>;
  }

  return (
    <div className="border border-slate-200 rounded-xl bg-white overflow-x-auto">
      <table className="w-full min-w-[820px]" data-testid="salary-month-table">
        <thead>
          <tr className="text-left text-xs font-semibold text-slate-500 uppercase bg-slate-50/70">
            <th className="px-4 py-3">Employee</th>
            <th className="px-4 py-3">Currency</th>
            <th className="px-4 py-3 text-right">Salary Due</th>
            <th className="px-4 py-3 text-right">Paid</th>
            <th className="px-4 py-3 text-right">Balance</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3 text-right"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const key = `${r.employee_id}-${r.kind}`;
            const expanded = !!open[key];
            return (
              <React.Fragment key={key}>
                <tr className="border-t border-slate-100" data-testid={`salary-row-${key}`}>
                  <td className="px-4 py-3">
                    <div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                      {r.employee_name}
                      {r.private && <Lock size={12} className="text-slate-400" title="China Office — private" />}
                    </div>
                    <div className="text-xs text-slate-500">
                      {r.kind === "quarterly" ? "Quarterly Payment / Incentive" : "Monthly salary"}{!r.active && " · Inactive"}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700">{r.currency}</td>
                  <td className="px-4 py-3 text-sm text-right tabular text-slate-900">{fmtMoney(r.due)}</td>
                  <td className="px-4 py-3 text-sm text-right tabular text-emerald-700">{fmtMoney(r.paid)}</td>
                  <td className={`px-4 py-3 text-sm text-right tabular font-semibold ${r.balance > 0 ? "text-amber-700" : "text-slate-900"}`}>{fmtMoney(r.balance)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-medium ${STATUS_TONE[r.status]}`} data-testid={`salary-status-${key}`}>{r.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {r.payments.length > 0 && (
                      <button type="button" onClick={() => setOpen((o) => ({ ...o, [key]: !expanded }))} data-testid={`salary-toggle-payments-${key}`}
                        className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 mr-3">
                        {r.payments.length} payment{r.payments.length > 1 ? "s" : ""} {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                    )}
                    {r.status !== "Paid" && (
                      <button type="button" onClick={() => setPayFor(r)} data-testid={`salary-pay-btn-${key}`}
                        className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-[#0055FF] hover:bg-[#0044CC] text-white text-xs font-semibold">
                        <Plus size={14} /> Record payment
                      </button>
                    )}
                  </td>
                </tr>
                {expanded && r.payments.map((p) => (
                  <tr key={p.id} className="bg-slate-50/60" data-testid={`salary-payment-${p.id}`}>
                    <td className="px-4 py-2 pl-8 text-xs text-slate-600 tabular">{p.payment_date}</td>
                    <td className="px-4 py-2 text-xs text-slate-600">{p.currency}</td>
                    <td className="px-4 py-2 text-xs text-slate-600" colSpan={2}>{p.company || "—"} · {p.account || "—"}{p.reference ? ` · Ref ${p.reference}` : ""}</td>
                    <td className="px-4 py-2 text-xs text-right tabular font-semibold text-slate-800">{fmtMoney(p.amount)}</td>
                    <td className="px-4 py-2 text-xs text-slate-500">{p.notes || ""}</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {p.transaction_id && (
                        <button type="button" onClick={() => nav(`/entry/${p.transaction_id}`)} title="Open linked transaction" data-testid={`salary-payment-tx-${p.id}`}
                          className="text-slate-400 hover:text-[#0055FF] mr-3"><ExternalLink size={14} /></button>
                      )}
                      <button type="button" onClick={() => removePayment(p)} data-testid={`salary-payment-delete-${p.id}`} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      {payFor && <PaymentDialog row={payFor} period={period} onClose={() => setPayFor(null)} onSaved={() => { setPayFor(null); onChanged(); }} />}
    </div>
  );
}
